import * as React from 'react';

import {Body} from 'shells/typography/body';

import type {
    GetActivityForModal_activity as Activity,
    ActivityUpdateInput,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {useGetExternalMeetings} from '../../external-meetings/tables/graphql/hooks';
import {refetchForEvent} from '../../lead/refetch-helpers';
import {useGetMeetingsSidebarCounts} from '../../sales/graphql/hooks';

import {useActivityModalContext, mapContextToCreateInput} from '../activity-modal-context';
import type {UpdateLocation} from '../types';

import {BulkCreateContactsFromAttendeesPopover} from './bulk-create-contacts-from-attendees/bulk-create-contacts-from-attendees-popover';
import {
    hasAtLeastOneAccountOrContactOnActivity,
    hasAtLeastOneExternalAttendeeOnActivity,
} from './helpers';

// @ts-expect-error
import {LeftSection, RightSection} from '.';

import styles from './footer.module.css';

type Props = {
    activity: Activity | null | undefined;
    handleUpdateActivity: (input: ActivityUpdateInput, location: UpdateLocation) => Promise<any>;
    onCancel: () => void;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    updateError: string | null | undefined;
    shouldPollForSync: boolean;
    setShouldPollForSync: (arg1: boolean) => void;
};

export function Footer(props: Props) {
    const {setHasUnsavedChanges, handleUpdateActivity, activity, setShouldPollForSync} = props;
    const [isLogging, setIsLogging] = React.useState<boolean>(false);
    const [
        isBulkCreateContactsFromAttendeesPopoverOpen,
        setIsBulkCreateContactsFromAttendeesPopoverOpen,
    ] = React.useState<boolean>(false);

    const saveButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const activityContext = useActivityModalContext();

    // We just want the refetch so that if we are on the meetings page and log
    // an activity, we can refetch the sidebar count and meetings for the table
    const {refetch: refetchUnloggedGoogleMeet} = useGetExternalMeetings({
        isLogged: false,
        serviceType: 'GOOGLE_MEET',
        skip: true,
    });
    const {refetch: refetchUnloggedZoom} = useGetExternalMeetings({
        isLogged: false,
        serviceType: 'ZOOM',
        skip: true,
    });
    const {refetch: refetchLoggedGoogleMeet} = useGetExternalMeetings({
        isLogged: true,
        serviceType: 'GOOGLE_MEET',
        skip: true,
    });
    const {refetch: refetchLoggedZoom} = useGetExternalMeetings({
        isLogged: true,
        serviceType: 'ZOOM',
        skip: true,
    });
    const {refetch: refetchMeetingsSidebarCounts} = useGetMeetingsSidebarCounts(true);

    // Allows for a brief window of time before the end time for this activity
    // to still be loggable, keeping us consistent with the backend
    const isFutureActivity = Boolean(activity && !activity.isLoggable);

    const handleLogActivity = React.useCallback(
        (externalAttendeeIds?: Array<string>) => {
            if (
                activity &&
                activity.isEditable &&
                (!Array.isArray(externalAttendeeIds) || externalAttendeeIds.length === 0) &&
                !hasAtLeastOneAccountOrContactOnActivity(activity) &&
                hasAtLeastOneExternalAttendeeOnActivity(activity)
            ) {
                setIsBulkCreateContactsFromAttendeesPopoverOpen(true);

                return;
            }

            setIsLogging(true);

            handleUpdateActivity(
                {
                    activity: (activity && activity.id) || '',
                    isLogged: true,
                    externalAttendees: Array.isArray(externalAttendeeIds)
                        ? externalAttendeeIds
                        : undefined,
                },
                'footer'
            )
                .then(() => {
                    if (NutClientConfig.hasLeadPageRedesignM3FF) {
                        refetchForEvent('activityStatusChanged');
                    } else {
                        Peep.reloadActivities();
                        // Updates stages carousel if we completed a stage goal
                        // @ts-expect-error
                        if (window.currentLead) {
                            // @ts-expect-error
                            window.currentLead.fetch();
                        }
                    }

                    // Dispatch event so any component that needs to refetch on
                    // activity logged can listen for this (ex: ActivitiesWidget)
                    window.dispatchEvent(new CustomEvent('activityLogged'));

                    // Refetches meetings sidebar counts and meetings for the table
                    // if we are on a meetings page
                    const windowPathname = window.location.pathname;
                    if (windowPathname.includes('/sales/meetings')) {
                        refetchMeetingsSidebarCounts();
                    }

                    if (windowPathname.includes('/sales/meetings/meet/unlogged-meetings')) {
                        refetchUnloggedGoogleMeet();
                    } else if (windowPathname.includes('/sales/meetings/zoom/unlogged-meetings')) {
                        refetchUnloggedZoom();
                    } else if (windowPathname.includes('/sales/meetings/meet/logged-meetings')) {
                        refetchLoggedGoogleMeet();
                    } else if (windowPathname.includes('/sales/meetings/zoom/logged-meetings')) {
                        refetchLoggedZoom();
                    }
                })
                .finally(() => {
                    setIsLogging(false);
                });
        },
        [
            handleUpdateActivity,
            activity,
            refetchUnloggedGoogleMeet,
            refetchUnloggedZoom,
            refetchLoggedGoogleMeet,
            refetchLoggedZoom,
            refetchMeetingsSidebarCounts,
        ]
    );

    const handleSyncExistingActivity = React.useCallback(() => {
        handleUpdateActivity(
            {
                activity: (activity && activity.id) || '',
                isForceSync: true,
            },
            'footer'
        ).then(() => {
            Peep.reloadActivities();
            setShouldPollForSync(true);
        });
    }, [handleUpdateActivity, activity, setShouldPollForSync]);

    const handleCreateActivity = React.useCallback(() => {
        setHasUnsavedChanges(false);
        const activityToCreate = mapContextToCreateInput(activityContext);
        activityContext.createActivityForModal(activityToCreate);
        analyticsClient.track(analyticsClient.EVENTS.TRACK_ACTIVITY_CREATED_VIA_MODAL);
    }, [activityContext, setHasUnsavedChanges]);

    const shouldShowForceSyncOnCreate =
        !props.activity &&
        activityContext.isCalendarSyncEnabled &&
        activityContext.primaryCalendarExportRule === 'NONE' &&
        !activityContext.createError;

    const showUpdateSyncStatus =
        props.activity && props.activity.canUserSeeSyncStatus && !props.updateError;

    return (
        <>
            <div className={styles['activity-modal-footer']}>
                <LeftSection
                    activity={props.activity}
                    onCancel={props.onCancel}
                    showSyncStatus={showUpdateSyncStatus}
                    isSyncing={props.shouldPollForSync || false}
                    handleUpdateActivity={handleSyncExistingActivity}
                    isConnectedToExternalCalendar={
                        props.activity ? props.activity.isConnectedToExternalCalendar : false
                    }
                    calendarSyncExternalService={activityContext.calendarSyncExternalService}
                    isHidden={activityContext.createError || props.updateError}
                />
                <div className='flex gap-8 align-center'>
                    {activityContext.createError && (
                        <Body size='small' color='error'>
                            {activityContext.createError}
                        </Body>
                    )}
                    {props.updateError && (
                        <Body size='small' color='error'>
                            {props.updateError}
                        </Body>
                    )}
                    <RightSection
                        status={props.activity && props.activity.status}
                        handleLogActivity={handleLogActivity}
                        handleCreateActivity={handleCreateActivity}
                        isCreatingActivity={activityContext.isCreatingActivity}
                        logOnCreate={activityContext.logOnCreate}
                        isSavingActivity={isLogging}
                        createError={Boolean(activityContext.createError)}
                        updateError={Boolean(props.updateError)}
                        canUserLogActivity={props.activity && props.activity.canUserLogActivity}
                        isFutureActivity={isFutureActivity}
                        shouldShowForceSync={shouldShowForceSyncOnCreate}
                        isFollowUpActivity={Boolean(activityContext.previousActivity)}
                        isPrivate={props.activity && props.activity.isPrivate}
                        // @ts-expect-error
                        getSaveButtonRef={(ref) => {
                            saveButtonRef.current = ref;
                        }}
                        isBulkCreatingContacts={isBulkCreateContactsFromAttendeesPopoverOpen}
                    />
                </div>
            </div>
            {isBulkCreateContactsFromAttendeesPopoverOpen &&
                saveButtonRef.current &&
                props.activity &&
                props.activity.participants && (
                    <BulkCreateContactsFromAttendeesPopover
                        anchor={saveButtonRef.current}
                        handleLogActivity={handleLogActivity}
                        participants={props.activity.participants}
                        onClose={() => setIsBulkCreateContactsFromAttendeesPopoverOpen(false)}
                    />
                )}
        </>
    );
}
